import React from "react";
import moment from "moment";
import { Table, Button, Tag, Alert, Message, Modal, Input, Popconfirm } from "@arco-design/web-react";
import EmptyState from "../../Components/EmptyState";
import { IconPlus, IconRefresh, IconDelete } from "@arco-design/web-react/icon";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { FetchApi, PostApi, DeleteApi } from "../../Api/AuthenticatedApi";
import DnsSetupGuide, { improvements } from "./DnsSetupGuide";

// How long a verified domain's senders keep working after its records go
// missing (the server's sendingdomain.LostGrace).
const GRACE_HOURS = 72;

// Part of the server's error for a domain set up in Amazon outside the app
// (sendingdomain.ErrDomainInUse), which a platform admin can take over.
const SET_UP_ELSEWHERE = "already set up for sending on our side";
// Part of the server's error for one of Contact Anchor's own domains
// (sendingdomain.ErrOwnDomain), which a platform admin can add anyway.
const OWN_DOMAIN = "Contact Anchor's own domain";

// domainStatus is what the status column says about a domain.
export function domainStatus(d) {
  if (d.verified_date && d.lost_date) {
    const stop = moment(d.lost_date).add(GRACE_HOURS, "hours").format("MMM D, h:mm a");
    return { color: "orange", text: `Records missing: its senders stop ${stop}` };
  }
  if (d.verified_date) return { color: "green", text: "Verified" };
  if (d.ses_status === "Failed") return { color: "red", text: "Amazon stopped checking: press Retry" };
  return { color: "orange", text: "Waiting for DNS records" };
}

// Settings → Sending Domains: the domains an org sends from. Once a domain's
// records are found, any address on it can be a sender, with no confirm emails.
// focus ({ domain }, a new object each time) reloads the list and opens that
// domain's DNS records: Sender Emails sets it after adding the domain.
export default function SendingDomains({ focus }) {
  const { getAccessTokenSilently } = useAuth0();
  const history = useNavigate();
  const [domains, setDomains] = React.useState([]);
  const [refresh, setRefresh] = React.useState(true);
  const [addOpen, setAddOpen] = React.useState(false);
  const [newDomain, setNewDomain] = React.useState("");
  const [addError, setAddError] = React.useState("");
  const [busy, setBusy] = React.useState("");
  const [expanded, setExpanded] = React.useState([]);
  // The domain to open once the list has loaded (from focus).
  const [openDomain, setOpenDomain] = React.useState("");

  React.useEffect(() => {
    if (!focus) return;
    setOpenDomain(focus.domain);
    setRefresh(true);
  }, [focus]);

  React.useEffect(() => {
    if (!openDomain || refresh) return;
    const d = domains.find((x) => x.domain === openDomain);
    if (d) setExpanded((keys) => (keys.includes(d.id) ? keys : [...keys, d.id]));
    setOpenDomain("");
  }, [openDomain, refresh, domains]);
  // Platform admin, from /api/me: only decides whether "Take over" shows; the
  // API checks its own list.
  const [platformAdmin, setPlatformAdmin] = React.useState(false);

  React.useEffect(() => {
    FetchApi(getAccessTokenSilently, "/api/me",
      (data) => setPlatformAdmin(!!(data && data.platform_admin)), () => {}, () => {});
  }, [getAccessTokenSilently]);

  React.useEffect(() => {
    if (!refresh) return;
    FetchApi(getAccessTokenSilently, "/api/settings/sending-domains/",
      (data) => { setDomains(data || []); setRefresh(false); },
      () => history("/login"),
      () => { setRefresh(false); Message.error("Failed to load sending domains"); });
  }, [refresh, getAccessTokenSilently, history]);

  const replace = (d) => setDomains((all) => all.map((x) => (x.id === d.id ? d : x)));

  // add adds the typed domain; takeOver (platform admins) adds one set up in
  // Amazon outside the app.
  function add(takeOver) {
    setBusy("add");
    setAddError("");
    PostApi(getAccessTokenSilently, takeOver ? "/api/settings/sending-domains/take-over" : "/api/settings/sending-domains/",
      (d) => {
        setBusy("");
        setAddOpen(false);
        setNewDomain("");
        setRefresh(true);
        Message.success(`${d.domain} added. Add the DNS records below, then press Check now.`);
      },
      () => history("/login"),
      (err) => { setBusy(""); setAddError((err && err.message) || "Failed to add the domain"); },
      { domain: newDomain });
  }
  const ownDomain = addError.includes(OWN_DOMAIN);
  const canTakeOver = platformAdmin && (ownDomain || addError.includes(SET_UP_ELSEWHERE));

  function act(d, action) {
    setBusy(d.id + action);
    PostApi(getAccessTokenSilently, `/api/settings/sending-domains/${d.id}/${action}`,
      (updated) => {
        setBusy("");
        replace(updated);
        if (updated.verified_date && !d.verified_date) Message.success(`${d.domain} is verified. Any address on it can send now.`);
        else if (!updated.verified_date) Message.info("Not all the records are found yet. DNS changes can take a while; we keep checking every few minutes.");
      },
      () => history("/login"),
      (err) => { setBusy(""); Message.error((err && err.message) || "Failed to check the domain"); });
  }

  function remove(d) {
    DeleteApi(getAccessTokenSilently, `/api/settings/sending-domains/${d.id}`,
      () => { setRefresh(true); Message.success(`${d.domain} removed`); },
      () => history("/login"),
      (err) => Message.error((err && err.message) || "Failed to remove the domain"));
  }

  const columns = [
    { title: "Domain", dataIndex: "domain" },
    {
      title: "Status",
      render: (_, d) => {
        const s = domainStatus(d);
        const left = d.lost_date ? [] : improvements(d);
        return (
          <span>
            <Tag color={s.color}>{s.text}</Tag>
            {left.length > 0 && (
              <Tag color="orange" style={{ marginLeft: 4 }} title={left.map((x) => x.label).join(", ")}>
                {left.length} to improve
              </Tag>
            )}
          </span>
        );
      },
    },
    {
      title: "Last checked",
      render: (_, d) => (d.last_checked_date ? moment(d.last_checked_date).fromNow() : "Not yet"),
    },
    {
      title: "",
      render: (_, d) => (
        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
          {d.ses_status === "Failed" && (
            <Button size="small" type="text" loading={busy === d.id + "retry"} onClick={() => act(d, "retry")}>Retry</Button>
          )}
          <Button size="small" type="text" icon={<IconRefresh />} loading={busy === d.id + "check"} onClick={() => act(d, "check")}>
            Check now
          </Button>
          <Popconfirm
            title={`Remove ${d.domain}?`}
            content="Senders that rely on it stop working until they're confirmed another way."
            onOk={() => remove(d)}
          >
            <Button shape="circle" type="text" icon={<IconDelete />} aria-label={`Remove ${d.domain}`} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <Alert type="info" content={
        "Send from your own domain (like yourbusiness.com) so your mail lands in the inbox, not spam. " +
        "Add the domain, add the DNS records we show you, and once they're found any address on the " +
        "domain can be a sender, with no confirm emails."
      } />
      <Table
        size="small"
        style={{ marginTop: 12 }}
        columns={columns}
        data={domains}
        rowKey="id"
        pagination={false}
        border={false}
        expandedRowRender={(d) => <DnsSetupGuide domain={d} onCheck={() => act(d, "check")} checking={busy === d.id + "check"} />}
        expandedRowKeys={expanded}
        onExpand={(d, open) => setExpanded((keys) => (open ? [...keys, d.id] : keys.filter((k) => k !== d.id)))}
        noDataElement={
          <EmptyState description="No sending domains yet">
            <Button type="primary" style={{ marginTop: 12 }} onClick={() => setAddOpen(true)}>Add your domain</Button>
          </EmptyState>
        }
      />
      {/* With none yet, the empty message has its own button. */}
      {domains.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Button type="primary" icon={<IconPlus />} onClick={() => setAddOpen(true)}>Add domain</Button>
        </div>
      )}
      <Modal
        visible={addOpen}
        title="Add a sending domain"
        okText="Add"
        onOk={() => add(false)}
        confirmLoading={busy === "add"}
        okButtonProps={{ disabled: !newDomain.trim() }}
        onCancel={() => { setAddOpen(false); setAddError(""); }}
      >
        <Input autoFocus placeholder="yourbusiness.com" value={newDomain} onChange={setNewDomain} onPressEnter={() => add(false)} />
        <div style={{ fontSize: 12, color: "var(--color-text-3)", marginTop: 6 }}>
          The part after the @ in the address you want to send from.
        </div>
        {addError && <div style={{ fontSize: 12, color: "rgb(var(--red-6))", marginTop: 6 }}>{addError}</div>}
        {canTakeOver && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 6 }}>
              {ownDomain
                ? "Platform admin: you can add it to this organization, for sending Contact Anchor's own mail. Every address on it can then send from this organization. Removing it later leaves it set up in Amazon, since the app's own emails use it too."
                : "Platform admin: this domain was set up in Amazon outside the app. Taking it over adds it to this organization and keeps its signing (DKIM) records, so any already in its DNS stay good. It's verified once this organization's ownership record is in its DNS."}
            </div>
            <Button status="warning" loading={busy === "add"} onClick={() => add(true)}>{ownDomain ? "Add anyway" : "Take over this domain"}</Button>
          </div>
        )}
      </Modal>
    </React.Fragment>
  );
}
